import type { IconName } from '@/shared/components/ui/Icon';
import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

export const HOME_TABS = ['starred', 'recent', 'files', 'shared', 'library', 'trash'] as const;
export const HOME_TAB_ICONS: Record<(typeof HOME_TABS)[number], IconName> = {
  starred: 'star',
  recent: 'clock',
  files: 'folder',
  shared: 'users',
  library: 'grid',
  trash: 'trash',
};

export type HomeTab = (typeof HOME_TABS)[number];

/** Where each tab lives; every one can be typed or bookmarked. My files and
 *  Trash keep the `/folders` addresses so a folder link (`/folders/<id>`) can
 *  be sent to someone. Starred is also `/starred`; `/` stays its home. */
const TAB_PATH: Record<HomeTab, string> = {
  starred: '/',
  recent: '/recent',
  files: '/folders',
  shared: '/shared',
  library: '/library',
  trash: '/folders/trash',
};

/** The open tab and folder, both read from the URL. `folderId` is `null`
 *  outside a folder. */
export function useHomeTab(): { tab: HomeTab; folderId: string | null; setTab: (tab: HomeTab) => void } {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();

  let tab: HomeTab = 'starred';
  let folderId: string | null = null;
  const folders = /^\/folders(?:\/([^/]+))?\/?$/.exec(pathname);
  if (folders) {
    if (folders[1] === 'trash') tab = 'trash';
    else {
      tab = 'files';
      folderId = folders[1] ? decodeURIComponent(folders[1]) : null;
    }
  } else if (pathname === '/starred' || pathname === '/recent' || pathname === '/shared' || pathname === '/library') {
    tab = pathname.slice(1) as HomeTab;
  } else {
    // Older `?tab=` links still land.
    const raw = new URLSearchParams(search).get('tab');
    if (raw === 'recent' || raw === 'shared') tab = raw;
  }

  const setTab = useCallback((next: HomeTab) => navigate(TAB_PATH[next]), [navigate]);
  return { tab, folderId, setTab };
}
